import React from 'react';
import { AlertTriangle, Info, Clock, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/**
 * Amber banner + list component for surfacing time entries / adjustments
 * that a human needs to review BEFORE payroll. Payroll-sacred rule: these
 * records are never silently skipped or auto-fixed by the server.
 *
 * Props:
 *   unresolvedEntries: array
 *   unresolvedAdjustments: array (optional)
 *   onMarkReviewed: async (type, id, note) => void   — optional, admin-only
 *   onEditEntry: (id) => void                         — optional, opens edit form
 *   isAdmin: boolean                                  — whether to show the actions
 */
const ISSUE_LABELS = {
  missing_clock_in: { label: 'Missing clock-in', color: 'red' },
  invalid_clock_in_format: { label: 'Bad clock-in timestamp', color: 'red' },
  invalid_clock_out_format: { label: 'Bad clock-out timestamp', color: 'red' },
  negative_duration: { label: 'Clock-out before clock-in', color: 'red' },
  missing_clock_out_open_shift: { label: 'Shift still open', color: 'amber' },
  non_numeric_hours: { label: 'Non-numeric hours', color: 'red' },
  missing_hours: { label: 'Missing hours', color: 'red' },
  invalid_date_format: { label: 'Bad date format', color: 'red' },
};

function formatTimestamp(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString();
  } catch {
    return String(val);
  }
}

export const UnresolvedEntriesAlert = ({
  unresolvedEntries = [],
  unresolvedAdjustments = [],
  onMarkReviewed,
  onEditEntry,
  isAdmin = false,
}) => {
  const totalCount = unresolvedEntries.length + unresolvedAdjustments.length;
  if (totalCount === 0) return null;

  return (
    <div
      className="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50 overflow-hidden"
      data-testid="unresolved-entries-alert"
    >
      {/* Banner */}
      <div className="flex items-start gap-3 p-4 border-b border-amber-300">
        <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-amber-900">
            {totalCount} time {totalCount === 1 ? 'record needs' : 'records need'} review before payroll
          </p>
          <p className="text-sm text-amber-800 mt-1">
            These entries were flagged by the system and are <strong>excluded from totals</strong> until a human confirms them. No data has been modified or removed.
          </p>
        </div>
      </div>

      {/* Entries list */}
      {unresolvedEntries.length > 0 && (
        <div className="divide-y divide-amber-200">
          <div className="px-4 py-2 bg-amber-100 text-xs font-semibold text-amber-900 uppercase tracking-wide">
            <Clock className="inline h-3 w-3 mr-1" />
            Time Entries ({unresolvedEntries.length})
          </div>
          {unresolvedEntries.map((e) => {
            const issueMeta = ISSUE_LABELS[e.issue] || { label: e.issue, color: 'red' };
            return (
              <div
                key={e.time_entry_id}
                className="px-4 py-3 text-sm"
                data-testid={`unresolved-entry-${e.time_entry_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {e.employee_name && (
                        <span className="font-medium text-gray-900">{e.employee_name}</span>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          issueMeta.color === 'red'
                            ? 'border-red-400 text-red-700 bg-red-50'
                            : 'border-amber-500 text-amber-800 bg-amber-100'
                        }`}
                      >
                        {issueMeta.label}
                      </Badge>
                    </div>
                    <p className="text-gray-700 mt-1 text-xs">{e.description}</p>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 font-mono">
                      <div>
                        <span className="text-gray-500">Clock in:</span>{' '}
                        {formatTimestamp(e.clock_in)}
                      </div>
                      <div>
                        <span className="text-gray-500">Clock out:</span>{' '}
                        {e.clock_out ? formatTimestamp(e.clock_out) : <em className="text-red-600">— none —</em>}
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {onEditEntry && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEditEntry(e.time_entry_id)}
                          data-testid={`btn-edit-${e.time_entry_id}`}
                        >
                          Review & Edit
                        </Button>
                      )}
                      {onMarkReviewed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-amber-900"
                          onClick={() => {
                            const note = window.prompt(
                              'Add a review note (optional):',
                              ''
                            );
                            if (note !== null) {
                              onMarkReviewed('entry', e.time_entry_id, note);
                            }
                          }}
                          data-testid={`btn-mark-reviewed-${e.time_entry_id}`}
                        >
                          Mark Reviewed
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Adjustments list */}
      {unresolvedAdjustments.length > 0 && (
        <div className="divide-y divide-amber-200 border-t-2 border-amber-300">
          <div className="px-4 py-2 bg-amber-100 text-xs font-semibold text-amber-900 uppercase tracking-wide">
            <Calendar className="inline h-3 w-3 mr-1" />
            Time Adjustments ({unresolvedAdjustments.length})
          </div>
          {unresolvedAdjustments.map((a) => {
            const issueMeta = ISSUE_LABELS[a.issue] || { label: a.issue, color: 'red' };
            return (
              <div
                key={a.adjustment_id}
                className="px-4 py-3 text-sm"
                data-testid={`unresolved-adjustment-${a.adjustment_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.employee_name && (
                        <span className="font-medium text-gray-900">{a.employee_name}</span>
                      )}
                      <Badge
                        variant="outline"
                        className="text-xs border-red-400 text-red-700 bg-red-50"
                      >
                        {issueMeta.label}
                      </Badge>
                    </div>
                    <p className="text-gray-700 mt-1 text-xs">{a.description}</p>
                    <div className="mt-2 text-xs text-gray-600 font-mono">
                      <div>
                        <span className="text-gray-500">Date:</span>{' '}
                        {a.adjustment_date || '—'}
                      </div>
                      <div>
                        <span className="text-gray-500">Hours:</span>{' '}
                        {String(a.adjustment_hours ?? '—')}
                      </div>
                      {a.reason && (
                        <div>
                          <span className="text-gray-500">Reason:</span> {a.reason}
                        </div>
                      )}
                    </div>
                  </div>

                  {isAdmin && onMarkReviewed && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-amber-900 flex-shrink-0"
                      onClick={() => {
                        const note = window.prompt(
                          'Add a review note (optional):',
                          ''
                        );
                        if (note !== null) {
                          onMarkReviewed('adjustment', a.adjustment_id, note);
                        }
                      }}
                      data-testid={`btn-mark-reviewed-adj-${a.adjustment_id}`}
                    >
                      Mark Reviewed
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Non-admin footer hint */}
      {!isAdmin && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-700 flex items-center gap-1">
          <Info className="h-3 w-3" />
          Your admin/accounts team will resolve these before payroll.
        </div>
      )}
    </div>
  );
};

export default UnresolvedEntriesAlert;
